import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CollectionService } from '@modules/coletas/services/collection.service';
import { IndicatorsService } from '@modules/indicadores/services/indicators.service';
import { Indicador, UserCollectionTask } from '@core/models/indicator.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { Drawer } from 'primeng/drawer';

@Component({
  selector: 'app-collection-dashboard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, CommonModule, RouterLink, ButtonModule, DialogModule, TagModule, TooltipModule, DatePicker, Drawer],
  templateUrl: './collection-dashboard.html',
  styleUrl: './collection-dashboard.css',
})
export class CollectionDashboard implements OnInit {
  private readonly router = inject(Router);
  private readonly collectionService = inject(CollectionService);
  protected readonly authService = inject(AuthService);
  private readonly indicatorsService = inject(IndicatorsService);

  protected readonly showMobileMenu = signal(false);
  protected readonly tasks = this.collectionService.pendingTasks;
  protected readonly historicalTasks = this.collectionService.historicalTasks;
  protected readonly loading = this.collectionService.loading;
  protected readonly role = this.authService.role;
  protected readonly submittingId = signal<number | null>(null);
  protected readonly error = signal('');
  protected readonly taskToConfirm = signal<UserCollectionTask | null>(null);
  
  protected readonly activeTab = signal<'pendentes' | 'concluidas' | 'historico_alteracoes'>('pendentes');
  protected readonly editingHistoryId = signal<number | null>(null);
  protected readonly editingValue = signal<number | null>(null);

  // Filters state
  protected readonly filterIndicatorId = signal<string>('');
  protected readonly filterMonth = signal<string>('');
  protected readonly filterMonthDate = signal<Date | null>(null);

  // Manual insertion modal state (ADMIN)
  protected readonly showInsertModal = signal(false);
  protected readonly submittingInsert = signal(false);
  protected readonly insertError = signal('');
  protected readonly insertForm = signal({
    indicadorId: '',
    mesAno: '',
    valor: null as number | null
  });

  protected readonly indicators = this.indicatorsService.indicators;
  
  protected readonly activeIndicators = computed(() => 
    this.indicators().filter(i => i.ativo !== false)
  );
  protected readonly selectedInsertIndicator = computed(() =>
    this.indicators().find(i => String(i.id) === String(this.insertForm().indicadorId)) ?? null
  );

  protected readonly filteredTasks = computed(() => {
    let list = this.tasks();
    const indId = this.filterIndicatorId();
    const month = this.filterMonth();

    if (indId) {
      list = list.filter(t => String(t.indicadorId) === String(indId));
    }
    if (month) {
      list = list.filter(t => t.mesAno && t.mesAno.startsWith(month));
    }
    return list;
  });

  protected readonly filteredHistoricalTasks = computed(() => {
    let list = this.historicalTasks();
    const indId = this.filterIndicatorId();
    const month = this.filterMonth();

    if (indId) {
      list = list.filter(t => String(t.indicadorId) === String(indId));
    }
    if (month) {
      list = list.filter(t => t.mesAno && t.mesAno.startsWith(month));
    }
    return list;
  });

  async ngOnInit() {
    await this.collectionService.loadPendingTasks();
    await this.indicatorsService.loadAll();
  }

  async setTab(tab: 'pendentes' | 'concluidas' | 'historico_alteracoes') {
    this.activeTab.set(tab);
    if (tab === 'concluidas' && this.historicalTasks().length === 0) {
      await this.collectionService.loadHistoricalTasks();
    }
  }

  clearFilters() {
    this.filterIndicatorId.set('');
    this.filterMonth.set('');
    this.filterMonthDate.set(null);
  }

  onMonthSelect(date: Date | null) {
    this.filterMonthDate.set(date);
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      this.filterMonth.set(`${y}-${m}`);
    } else {
      this.filterMonth.set('');
    }
  }

  async logout() {
    await this.authService.logout();
    this.router.navigate(['/login']);
  }

  openConfirmation(task: UserCollectionTask) {
    if (task.valor === null || task.valor === undefined) return;
    this.taskToConfirm.set(task);
  }

  async confirmSave() {
    const task = this.taskToConfirm();
    if (!task) return;

    this.submittingId.set(task.coletaId);
    this.error.set('');
    try {
      if (task.valor === null) throw new Error('Valor inválido');
      await this.collectionService.saveCollection(task.coletaId, task.valor as number);
      
      if (this.activeTab() === 'pendentes') {
        this.collectionService.removeTaskFromList(task.coletaId);
        await this.collectionService.loadHistoricalTasks();
      } else {
        this.editingHistoryId.set(null);
        this.editingValue.set(null);
        await this.collectionService.loadHistoricalTasks();
      }
      this.taskToConfirm.set(null);
    } catch (err: any) {
      this.error.set(err.message || 'Erro ao salvar coleta');
    } finally {
      this.submittingId.set(null);
    }
  }

  editHistoricalTask(task: UserCollectionTask) {
    this.editingHistoryId.set(task.coletaId);
    this.editingValue.set(task.valor);
  }

  cancelEditHistorical() {
    this.editingHistoryId.set(null);
    this.editingValue.set(null);
  }

  openConfirmationHistorical(task: UserCollectionTask) {
    this.openConfirmation({ ...task, valor: this.editingValue() });
  }

  cancelConfirm() {
    this.taskToConfirm.set(null);
  }

  formatMonth(dateStr?: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length < 2) return dateStr;
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const monthIdx = parseInt(parts[1], 10) - 1;
    return `${months[monthIdx] || parts[1]} / ${parts[0]}`;
  }

  formatReference(task?: UserCollectionTask | null): string {
    if (!task) return '';
    return task.periodicidade === 'ANUAL' ? String(task.anoReferencia ?? '') : this.formatMonth(task.mesAno);
  }

  isExpired(mesAno?: string): boolean {
    if (!mesAno) return false;
    
    try {
      const date = new Date(mesAno + (mesAno.includes('T') ? '' : 'T00:00:00'));
      if (isNaN(date.getTime())) return false;
      const deadline = new Date(date.getFullYear(), date.getMonth() + 1, 23, 23, 59, 59);
      return new Date() > deadline;
    } catch {
      return false;
    }
  }

  getDeadline(mesAno?: string): string {
    if (!mesAno) return '';
    try {
      const date = new Date(mesAno + (mesAno.includes('T') ? '' : 'T00:00:00'));
      if (isNaN(date.getTime())) return '';
      date.setMonth(date.getMonth() + 1);
      
      const months = [
        'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
      ];
      return `23 de ${months[date.getMonth()] || ''}`;
    } catch {
      return '';
    }
  }

  openInsertModal() {
    this.insertError.set('');
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.insertForm.set({
      indicadorId: '',
      mesAno: currentMonth,
      valor: null
    });
    this.showInsertModal.set(true);
  }

  closeInsertModal() {
    this.showInsertModal.set(false);
  }

  selectInsertIndicator(indicadorId: string) {
    const indicator = this.indicators().find(i => String(i.id) === String(indicadorId));
    const now = new Date();
    const referencia = indicator?.periodicidade === 'ANUAL'
      ? String(now.getFullYear())
      : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.insertForm.update(form => ({ ...form, indicadorId, mesAno: referencia }));
  }

  async saveInsert() {
    const form = this.insertForm();
    if (!form.indicadorId || !form.mesAno || form.valor === null) {
      this.insertError.set('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    this.submittingInsert.set(true);
    this.insertError.set('');
    try {
      await this.collectionService.saveNewHistoricalRecord(
        form.indicadorId,
        this.selectedInsertIndicator()?.periodicidade || 'MENSAL',
        form.mesAno,
        form.valor
      );
      
      await this.collectionService.loadPendingTasks();
      await this.collectionService.loadHistoricalTasks();
      
      this.closeInsertModal();
      alert('Coleta histórica lançada com sucesso!');
    } catch (err: any) {
      this.insertError.set(err.message || 'Erro ao lançar coleta histórica.');
    } finally {
      this.submittingInsert.set(false);
    }
  }
}
